import { ActionTree } from 'vuex';
import router from '../../../router/index';
import ApiUrl from '../../../api/url';
import { NodeGroupState, NewNodeGroup, EditNodeGroup, SelectedNodes } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { isHappenedOtherPartyError } from '../../error-type';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<NodeGroupState, RootState> = {
    /**
     * ノードグループ一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { pageNumber, perPage, sort } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.INDEX_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.NodeGroup.INDEX, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.INDEX_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.INDEX_FAILURE, resData.errors[0]);
        }
    },

    /**
     * SAMLサービスプロバイダのノードが紐づくノードグループ一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async searchSaml({ dispatch, commit }) {
        try {
            commit(types.INDEX_SAML_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.NodeGroup.INDEX_SAML },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.INDEX_SAML_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.INDEX_SAML_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノードグループ新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async new({ commit }) {
        commit(types.NEW_NODE_GROUP);
    },

    /**
     * ノードグループ新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewNodeGroup} newNodeGroup 新規ノードグループ情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newNodeGroup: NewNodeGroup) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.NodeGroup.CREATE, params: newNodeGroup },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeGroup.CREATED);
                dispatch('toastMessage', `${newNodeGroup.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * ノードグループの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ノードグループID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.NodeGroup.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ノードグループ更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {EditNodeGroup} editNodeGroup 更新するノードグループ情報
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, editNodeGroup: EditNodeGroup) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.NodeGroup.UPDATE.replace(':id', editNodeGroup.id.toString()),
                    params: editNodeGroup,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeGroup.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * ノードグループ削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id ノードグループID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.NodeGroup.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeGroup.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * 所属ノード追加前のエラー等のクリア
     * 前回にノード追加に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async newNodes({ commit }) {
        commit(types.NEW_NODES);
    },

    /**
     * 所属ノード追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedNodes} newNodes 追加する所属ノード情報
     * @returns {Promise<void>}
     */
    async addNodes({ dispatch, commit, rootState }, newNodes: SelectedNodes) {
        const payload = {
            id: newNodes.id,
            node_ids: newNodes.nodeIds,
        };
        try {
            commit(types.ADD_NODES_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.NodeGroup.ADD_NODES.replace(':id', newNodes.id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ADD_NODES_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeGroup.ADD_NODES);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.ADD_NODES_FAILURE, params);
        }
    },

    /**
     * 所属ノード除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {SelectedNodes} removeNodes 除外するノード情報
     * @returns {Promise<void>}
     */
    async removeNodes({ dispatch, commit, rootState }, removeNodes: SelectedNodes) {
        const payload = {
            node_ids: removeNodes.nodeIds,
        };
        try {
            commit(types.REMOVE_NODES_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.NodeGroup.REMOVE_NODES.replace(':id', removeNodes.id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REMOVE_NODES_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.NodeGroup.REMOVE_NODES);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REMOVE_NODES_FAILURE, params);
        }
    },

    /**
     * ノードグループ全件取得
     * @returns {Promise<void>}
     */
    async fetchAll({ dispatch, commit }) {
        try {
            commit(types.INDEX_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.NodeGroup.INDEX_ALL },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.INDEX_SUCCESS, Object.freeze(resData));
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.INDEX_FAILURE, resData.errors[0]);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { sort } = {}) {
        try {
            const payload = {
                sort: sort,
            };
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.NodeGroup.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS, resData);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
